import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import * as Sentry from '@sentry/nuxt';
import { defineStore } from 'pinia';

import type { Customer, CustomerUpdateRequest } from '~/stores/customer/types';

import { ApiMethod, handleApiError } from '~/utils/apiClient';

import type { Address } from '../address/types';

const STORE_ID = 'customer';

interface Actions {
  createAccount: (email: string, password: string, firstname?: string, lastname?: string) => Promise<Customer | null>;
  fetchCustomer: () => Promise<Customer | null>;
  updateCustomer: (data: CustomerUpdateRequest) => Promise<Customer | null>;
}

interface Getters extends _GettersTree<State> {
  getDefaultBillingAddress: (state: State) => Address | null;
  getDefaultShippingAddress: (state: State) => Address | null;
}

interface State {
  customer: Customer | null;
}

export const useCustomerStore = defineStore<typeof STORE_ID, State, Getters, Actions>(STORE_ID, {
  actions: {
    async createAccount(email: string, password: string, firstname?: string, lastname?: string) {
      try {
        const response = await this.$api.fetch<Customer>(ApiMethod.POST, '/customers/signup', {
          firstname,
          lastname,
          password,
          username: email,
        });

        if (response.data) {
          this.customer = response.data;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError, 'warning');

        return null;
      }

      return null;
    },
    async fetchCustomer() {
      try {
        const response = await this.$api.fetch<Customer>(ApiMethod.GET, '/customers/me', undefined, true);

        if (response.data) {
          this.customer = response.data;
          Sentry.setUser({ email: response.data.email });

          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }

      return null;
    },
    async updateCustomer(data: CustomerUpdateRequest) {
      try {
        const response = await this.$api.fetch<Customer>(ApiMethod.PUT, '/customers/me', { ...data }, true);

        if (response.data) {
          this.customer = response.data;

          return response.data;
        }

        return null;
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }
    },
  },
  getters: {
    getDefaultBillingAddress: (state) =>
      state.customer?.default_billing_address_id
        ? (state.customer.addresses?.find((address) => address.id === state.customer?.default_billing_address_id) ??
          null)
        : null,
    getDefaultShippingAddress: (state) =>
      state.customer?.default_shipping_address_id
        ? (state.customer.addresses?.find((address) => address.id === state.customer?.default_shipping_address_id) ??
          null)
        : null,
  },
  state: () => ({
    customer: null,
  }),
});
